import React, { useRef, useState } from 'react';
import { Handle, Position, useReactFlow } from '@reactflow/core';
import { NodeResizer } from '@reactflow/node-resizer';
import '@reactflow/node-resizer/dist/style.css';
import { User, Car, MapPin, Phone, FileText, Paperclip, Upload, Loader2 } from 'lucide-react';
import { Node as BoardNode, NODE_TYPES } from '../types';
import { storageService } from '../services/storageService';
import toast from 'react-hot-toast';
import { useBoard } from '../context/BoardContext';

interface CustomNodeProps {
  id: string;
  data: BoardNode;
  selected: boolean;
}

const CustomNode: React.FC<CustomNodeProps> = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();
  const { triggerSaveBoard } = useBoard();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const nodeType = NODE_TYPES[data.type];

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drag/select
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('Selected file for upload:', file.name, 'Size:', file.size);
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        console.warn('File too large for upload');
        toast.error('Image size must be less than 5MB');
        return;
      }

      setIsUploadingImage(true);
      const toastId = toast.loading('Uploading profile image...');

      try {
        const path = `images/nodes/${id}_${Date.now()}_${file.name}`;
        console.log('Attempting storageService.uploadFile with path:', path);
        const downloadUrl = await storageService.uploadFile(file, path);

        console.log('Upload successful! Updating node data with URL:', downloadUrl);
        setNodes((nds: any[]) =>
          nds.map((node) => {
            if (node.id === id) {
              return { ...node, data: { ...node.data, image: downloadUrl } };
            }
            return node;
          })
        );

        // Trigger save immediately for important changes like image uploads
        // Add specific delay to ensure React Flow internal state is updated before saving
        setTimeout(() => triggerSaveBoard(true), 100);

        toast.success('Profile image updated', { id: toastId });
      } catch (error: any) {
        console.error('Node image upload failed in CustomNode.tsx:', error);

        // Specific check for CORS/Preflight failures which are common in Firebase Storage custom domains
        const isCorsError = error.message?.includes('cross-origin') ||
          error.code?.includes('storage/unauthorized') ||
          error.message?.includes('failed to fetch');

        if (isCorsError) {
          toast.error(
            'Upload Blocked by CORS: Please run the gsutil command provided in the guide to authorize fibreboard.pro',
            { id: toastId, duration: 6000 }
          );
        } else {
          toast.error(`Failed to upload image: ${error.message || 'Unknown error'}`, { id: toastId });
        }
      } finally {
        console.log('Finishing upload process, resetting state.');
        setIsUploadingImage(false);
      }
    }
  };

  // Note: We rely on Tailwind 'dark' classes for theming instead of JS logic

  const getIcon = () => {
    switch (data.type) {
      case 'person': return <User size={16} />;
      case 'vehicle': return <Car size={16} />;
      case 'location': return <MapPin size={16} />;
      case 'phone': return <Phone size={16} />;
      default: return <FileText size={16} />;
    }
  };

  const isCircle = data.shape === 'circle';

  const isStickyNote = data.customEntityType === 'Note' || data.tags?.includes('note');

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative border-2 shadow-lg transition-all duration-300
        ${isCircle ? 'rounded-full aspect-square flex items-center justify-center' : 'rounded-lg'}
        ${selected ? 'border-blue-500 shadow-blue-500/20' : isStickyNote ? 'border-yellow-300 dark:border-yellow-600' : 'border-gray-200 dark:border-gray-700'}
        ${isStickyNote ? '' : 'bg-white dark:bg-slate-900/95'} backdrop-blur-sm
        group hover:border-gray-300 dark:hover:border-gray-600
        font-sans h-full w-full
      `}
      style={{
        borderLeftColor: data.color,
        borderLeftWidth: isCircle ? '2px' : '4px',
        ...(isStickyNote ? { backgroundColor: data.backgroundColor || '#FEF3C7' } : {}),
      }}
    >
      <NodeResizer
        color="#3b82f6"
        isVisible={selected && !!data.isResizable}
        minWidth={isCircle ? 120 : 200}
        minHeight={isCircle ? 120 : 80}
        keepAspectRatio={isCircle}
        handleStyle={{ width: 8, height: 8, borderRadius: '50%' }}
        onResizeEnd={() => {
          // Force an immediate save when resizing ends
          setTimeout(() => triggerSaveBoard(true), 100);
        }}
      />
      {/* Dynamic Handles - Bidirectional on all sides */}
      {/* Top */}
      <Handle type="target" position={Position.Top} id="t-t" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !top-[-6px]" />
      <Handle type="source" position={Position.Top} id="t-s" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !top-[-6px]" />

      {/* Bottom */}
      <Handle type="target" position={Position.Bottom} id="b-t" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !bottom-[-6px]" />
      <Handle type="source" position={Position.Bottom} id="b-s" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !bottom-[-6px]" />

      {/* Left */}
      <Handle type="target" position={Position.Left} id="l-t" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !left-[-6px]" />
      <Handle type="source" position={Position.Left} id="l-s" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !left-[-6px]" />

      {/* Right */}
      <Handle type="target" position={Position.Right} id="r-t" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !right-[-6px]" />
      <Handle type="source" position={Position.Right} id="r-s" className="w-3 h-3 !bg-white dark:!bg-slate-900 !border-blue-500 border-2 !right-[-6px]" />

      <div className={`${isCircle ? 'flex flex-col items-center justify-center text-center p-2' : 'p-3 space-y-2'}`}>
        {/* Header */}
        <div className={`flex items-center gap-3 ${isCircle ? 'flex-col gap-1' : ''}`}>
          <div
            className="text-gray-500 dark:text-slate-400 cursor-pointer hover:text-indigo-500 transition-colors relative group/icon"
            onClick={handleIconClick}
            title="Click to upload image"
          >
            {data.image ? (
              <div className={`${isCircle ? 'w-12 h-12 rounded-full' : 'w-10 h-10 rounded'} overflow-hidden border border-gray-200 dark:border-gray-700 relative group/img`}>
                <img src={data.image} alt={data.label} className="w-full h-full object-cover" />
                {isUploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 size={16} className="text-white animate-spin" />
                  </div>
                )}
                {!isUploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity">
                    <Upload size={12} className="text-white" />
                  </div>
                )}
              </div>
            ) : (
              <div className={`${isCircle ? 'w-10 h-10' : 'w-8 h-8'} flex items-center justify-center bg-gray-50 dark:bg-slate-800 rounded-full border border-gray-200 dark:border-gray-700 relative group/icon`}>
                {isUploadingImage ? <Loader2 size={16} className="text-blue-500 animate-spin" /> : getIcon()}
                {!isUploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover/icon:opacity-100 transition-opacity">
                    <Upload size={12} className="text-white" />
                  </div>
                )}
              </div>
            )}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          <div className={`flex-1 min-w-0 ${isCircle ? 'text-center w-full' : ''}`}>
            <div className={`text-sm font-semibold truncate ${isStickyNote ? 'text-gray-900' : 'text-gray-900 dark:text-gray-100'} ${isCircle ? 'px-1' : ''}`}>
              {data.label || 'Untitled'}
            </div>
            {!isCircle && (
              <div className={`text-xs font-mono uppercase tracking-wider ${isStickyNote ? 'text-gray-600' : 'text-gray-500 dark:text-slate-500'}`}>
                {data.customEntityType || nodeType.label}
              </div>
            )}
          </div>
          {!isCircle && data.attachments.length > 0 && (
            <div className="
              flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
              bg-gray-100 text-gray-600
              dark:bg-slate-800 dark:text-slate-400
            ">
              <Paperclip size={10} />
              {data.attachments.length}
            </div>
          )}
        </div>

        {/* Hover Popover Dialog */}
        {isHovered && !selected && (
          <div className="
            absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-[100]
            w-64 p-4 rounded-xl shadow-2xl border transition-all animate-in fade-in zoom-in duration-200
            bg-white/95 dark:bg-slate-900/95 backdrop-blur-md
            border-gray-200 dark:border-slate-700
            pointer-events-none
          ">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-gray-50 dark:bg-slate-800 text-blue-500 border border-gray-100 dark:border-slate-700">
                {getIcon()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                  {data.label || 'Untitled Entity'}
                </div>
                <div className="text-[10px] text-blue-500 font-mono uppercase tracking-tighter">
                  {data.customEntityType || nodeType.label}
                </div>
              </div>
            </div>

            {data.notesMd && (
              <div className="mb-3">
                <div className="text-[11px] text-gray-600 dark:text-slate-400 line-clamp-6 leading-relaxed whitespace-pre-wrap">
                  {data.notesMd.replace(/[#*`]/g, '')}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-slate-800/50">
              <div className="flex items-center gap-2">
                <Paperclip size={10} className="text-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-slate-500">{data.attachments.length} files</span>
              </div>
              {(data.tsStart || data.tsEnd) && (
                <div className="text-[9px] font-mono text-gray-400 dark:text-slate-500">
                  {data.tsStart && new Date(data.tsStart).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Pointer arrow */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-r border-b border-gray-200 dark:border-slate-700 rotate-45" />
          </div>
        )}

        {/* Tags (Condensed for static view) */}
        {!isCircle && data.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {data.tags.slice(0, 2).map((tag, index) => (
              <span
                key={index}
                className="
                  px-1.5 py-0.5 text-[9px] rounded font-medium
                  bg-gray-100 text-gray-500
                  dark:bg-slate-800 dark:text-slate-500
                  border border-transparent dark:border-slate-700
                "
              >
                {tag}
              </span>
            ))}
            {data.tags.length > 2 && (
              <span className="px-1.5 py-0.5 text-[9px] rounded font-medium bg-gray-50 text-gray-400 dark:bg-slate-800/50 dark:text-slate-600">
                +{data.tags.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Timeline indicator */}
        {(data.tsStart || data.tsEnd) && (
          <div className="
            text-[9px] pt-1.5 border-t mt-1.5 font-mono flex justify-between
            border-gray-50 text-gray-400
            dark:border-slate-800/50 dark:text-slate-500
          ">
            <span>{data.tsStart && new Date(data.tsStart).toLocaleDateString('en-US', { year: '2-digit', month: 'short' })}</span>
            {data.tsStart && data.tsEnd && data.tsStart !== data.tsEnd && <span>→</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomNode;
